import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router-dom';
import { PROTOKOLL_STATUS_LABEL, formatiereDatum, protokollNrAnzeige } from '@uep/shared';
import { Karte, Knopf, Leerzustand } from '@/components/ui';
import { db } from '@/offline/db';
import { useSync } from '@/offline/sync';
import { useAuth } from '@/features/auth/AuthKontext';

/**
 * Startbildschirm: was heute ansteht und was noch offen ist.
 *
 * Alle Zahlen kommen aus IndexedDB — der Bildschirm funktioniert offline
 * genauso wie online.
 */

const Kachel = ({
  titel,
  wert,
  hinweis,
  ziel,
  betont,
}: {
  titel: string;
  wert: number | string;
  hinweis?: string;
  ziel?: string;
  betont?: boolean;
}) => {
  const inhalt = (
    <div
      className={
        'rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200 transition-colors ' +
        (ziel ? 'hover:ring-marke-400 ' : '') +
        'dunkel:bg-slate-900 dunkel:ring-slate-700'
      }
    >
      <p className="text-sm text-slate-500 dunkel:text-slate-400">{titel}</p>
      <p
        className={
          'mt-1 text-3xl font-semibold ' +
          (betont ? 'text-amber-600 dunkel:text-amber-400' : 'text-slate-900 dunkel:text-slate-50')
        }
      >
        {wert}
      </p>
      {hinweis && <p className="mt-1 text-xs text-slate-500 dunkel:text-slate-400">{hinweis}</p>}
    </div>
  );

  return ziel ? <Link to={ziel}>{inhalt}</Link> : inhalt;
};

export const Uebersicht = () => {
  const { nutzer } = useAuth();
  const zustand = useSync();

  const entwuerfe = useLiveQuery(
    () =>
      db.protokolle
        .where('status')
        .equals('DRAFT')
        .and((p) => p.konfliktkopie === 0)
        .count(),
    [],
    0,
  );

  const objekte = useLiveQuery(() => db.objekte.where('aktiv').equals(1).count(), [], 0);

  const konflikte = useLiveQuery(
    () => db.protokolle.where('konfliktkopie').equals(1).count(),
    [],
    0,
  );

  const letzte = useLiveQuery(
    () =>
      db.protokolle
        .orderBy('lokalGeaendertAm')
        .reverse()
        .filter((protokoll) => protokoll.konfliktkopie === 0)
        .limit(6)
        .toArray(),
    [],
    [],
  );

  const heutigeTermine = useLiveQuery(
    async () => {
      const heute = new Date();
      const von = new Date(heute.getFullYear(), heute.getMonth(), heute.getDate()).toISOString();
      const bis = new Date(
        heute.getFullYear(),
        heute.getMonth(),
        heute.getDate() + 1,
      ).toISOString();
      return db.termine
        .where('beginn')
        .between(von, bis)
        .and((t) => t.erledigt === 0)
        .toArray();
    },
    [],
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dunkel:text-slate-50">
            Guten Tag{nutzer?.vorname ? `, ${nutzer.vorname}` : ''}
          </h1>
          <p className="mt-1 text-sm text-slate-600 dunkel:text-slate-400">
            {formatiereDatum(new Date())} — hier ist Ihr Stand.
          </p>
        </div>
        <Link to="/protokolle/neu">
          <Knopf groesse="gross">Neues Protokoll</Knopf>
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kachel titel="Offene Entwürfe" wert={entwuerfe} ziel="/protokolle?status=DRAFT" />
        <Kachel titel="Termine heute" wert={heutigeTermine.length} ziel="/termine" />
        <Kachel titel="Aktive Objekte" wert={objekte} ziel="/objekte" />
        <Kachel
          titel="Wartet auf Sync"
          wert={zustand.zaehler.gesamt}
          hinweis={zustand.online ? undefined : 'Gerät ist offline'}
          betont={zustand.zaehler.gesamt > 0}
        />
      </div>

      {konflikte > 0 && (
        <Karte
          titel="Konfliktkopien"
          beschreibung="Diese Entwürfe wurden anderswo bereits abgeschlossen."
        >
          <p className="text-sm text-slate-600 dunkel:text-slate-300">
            {konflikte} {konflikte === 1 ? 'Entwurf wurde' : 'Entwürfe wurden'} als Kopie gesichert,
            weil das Protokoll auf einem anderen Gerät bereits unterschrieben wurde. Bitte prüfen
            Sie, ob daraus noch etwas übernommen werden muss.
          </p>
          <Link to="/protokolle?konflikte=1" className="mt-3 inline-block">
            <Knopf aussehen="sekundaer" groesse="klein">
              Konflikte ansehen
            </Knopf>
          </Link>
        </Karte>
      )}

      <Karte titel="Zuletzt bearbeitet">
        {letzte.length === 0 ? (
          <Leerzustand
            titel="Noch keine Protokolle"
            text="Legen Sie das erste Protokoll an — Objekt und Vorlage wählen, den Rest füllt die App vor."
            aktion={
              <Link to="/protokolle/neu">
                <Knopf>Neues Protokoll</Knopf>
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-slate-200 dunkel:divide-slate-700">
            {letzte.map((protokoll) => (
              <li key={protokoll.id}>
                <Link
                  to={`/protokolle/${protokoll.id}`}
                  className="flex min-h-touch items-center justify-between gap-3 py-3"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-slate-900 dunkel:text-slate-100">
                      {protokollNrAnzeige(protokoll.protokollNr)} — {protokoll.art}
                    </span>
                    <span className="block text-sm text-slate-500 dunkel:text-slate-400">
                      {protokoll.uebergabeDatum
                        ? formatiereDatum(protokoll.uebergabeDatum)
                        : 'ohne Datum'}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dunkel:bg-slate-800 dunkel:text-slate-200">
                    {PROTOKOLL_STATUS_LABEL[protokoll.status]}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
};
